/**
 * Reference-integrity checks (design §18) — the by-name contract between level data and
 * the design's Content items, CHECKED instead of left to prompt etiquette. Only runs when
 * the caller supplies `ctx.palette` (the known Content names); severity is `warn` so a
 * palette that is momentarily stale doesn't block editing, but the repair loops and the
 * validation panel both surface it.
 */
import type { LevelFinding } from './types.js';

/** Findings for refs that don't resolve against the Content palette. */
export function checkRefs(
  refs: { ref: string; where: string }[],
  palette: ReadonlySet<string> | undefined,
): LevelFinding[] {
  if (!palette || palette.size === 0) return [];
  const findings: LevelFinding[] = [];
  for (const { ref, where } of refs) {
    if (!palette.has(ref)) {
      findings.push({
        severity: 'warn',
        code: 'orphan-ref',
        message: `${where} references "${ref}", which is not a Content item — fix the name or add the item to Content`,
      });
    }
  }
  return findings;
}

/** Collect ref usages from a spatial level (entities + vertex hazards). */
export function spatialRefs(level: {
  vertices: { id: string; hazard?: string }[];
  entities?: { id: string; ref: string }[];
  encounter?: { ref: string };
}): { ref: string; where: string }[] {
  return [
    ...level.vertices
      .filter((v): v is { id: string; hazard: string } => typeof v.hazard === 'string')
      .map((v) => ({ ref: v.hazard, where: `vertex "${v.id}" hazard` })),
    ...(level.entities ?? []).map((e) => ({ ref: e.ref, where: `entity "${e.id}"` })),
    ...(level.encounter ? [{ ref: level.encounter.ref, where: 'encounter' }] : []),
  ];
}

/** Collect ref usages from a topdown level (entities + room hazards). */
export function topdownRefs(level: {
  rooms: { id: string; hazard?: string }[];
  entities?: { id: string; ref: string }[];
}): { ref: string; where: string }[] {
  return [
    ...level.rooms
      .filter((r): r is { id: string; hazard: string } => typeof r.hazard === 'string')
      .map((r) => ({ ref: r.hazard, where: `room "${r.id}" hazard` })),
    ...(level.entities ?? []).map((e) => ({ ref: e.ref, where: `entity "${e.id}"` })),
  ];
}
